// Helpers for the My Listings components

import { asRawImageRef, type RawImageRef } from "@/lib/storage/public-url"

export function formatDate(dateString: string) {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date)
}

export function getConditionLabel(conditionValue?: string) {
  if (!conditionValue) return ""
  const CONDITIONS = [
    { value: "new", label: "New" },
    { value: "pre-owned", label: "Pre-owned" },
    { value: "custom", label: "Custom" },
  ]
  const condition = CONDITIONS.find((c) => c.value === conditionValue)
  return condition ? condition.label.split(" ")[0] : conditionValue
}

/**
 * `listings.image_urls` (array OR JSON string) → edit-dialog photo slots.
 *
 * Deliberately does NOT resolve: `preview` is the value the save path writes
 * back into the column (display ≠ persistence, handoff §9), so it is minted as
 * `RawImageRef` and render sites must call `resolveStorageUrl` themselves.
 * That is also why this helper is on the fitness pin's frozen legacy list —
 * `tests/unit/fitness-no-local-image-parsers.test.ts`.
 */
export function parseImages(images?: string[] | string): { file: null; preview: RawImageRef; photoId: string }[] {
  if (!images) {
    return []
  }
  
  // Helper function to generate safe photo ID
  const generateSafePhotoId = (url: string, index: number): string => {
    try {
      // Try to create a hash-like ID without btoa
      const urlHash = url
        .split('')
        .reduce((hash, char) => ((hash << 5) - hash + char.charCodeAt(0)) & 0xffffffff, 0)
        .toString(36)
        .substring(0, 8)
      return `existing-${index}-${urlHash}`
    } catch (error) {
      // Fallback to timestamp-based ID if hashing fails
      return `existing-${index}-${Date.now().toString(36)}`
    }
  }
  
  if (Array.isArray(images)) {
    return images
      .filter((url) => url !== "")
      .map((url, index) => ({ 
        file: null, 
        preview: asRawImageRef(url),
        photoId: generateSafePhotoId(url, index)
      }))
  }
  
  if (typeof images === "string") {
    try {
      const parsed = JSON.parse(images)
      
      if (Array.isArray(parsed)) {
        return parsed
          .filter((url: string) => url !== "")
          .map((url: string, index: number) => ({ 
            file: null, 
            preview: asRawImageRef(url),
            photoId: generateSafePhotoId(url, index)
          }))
      }
    } catch (err) {
      console.error("Error parsing image JSON:", err)
    }
  }
  
  return []
}
